import React from 'react';

/**
 * Top-level error boundary for the Liens app.
 *
 * Without this, if any descendant throws during render, React 18 unmounts
 * the entire app tree. The root App component then remounts and `useState`
 * defaults reset — which is what was wiping users back to the Dashboard
 * mid-flow. With this boundary in place, we instead show a friendly error
 * card, log the error to the console, and give the user a "Try again"
 * button. The error is also surfaced so we can see what actually went wrong
 * next time it happens.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log to the console so the developer (and Teresa) can see what threw.
    // Also surface it on window for easy retrieval via DevTools.
    // eslint-disable-next-line no-console
    console.error('Liens app caught an error:', error, errorInfo);
    try {
      window.__liensLastError = { error: String(error), stack: error?.stack, componentStack: errorInfo?.componentStack };
    } catch (e) { /* ignore */ }
    this.setState({ errorInfo });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const errMsg = this.state.error ? String(this.state.error) : 'Unknown error';
      return (
        <div style={{
          minHeight: '100vh',
          background: '#F3F3F3',
          padding: '48px 16px',
          fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
          color: '#171D29',
        }}>
          <div style={{
            maxWidth: 560,
            margin: '0 auto',
            background: 'white',
            borderRadius: 6,
            boxShadow: '0 2px 12px rgba(23,29,41,0.08)',
            overflow: 'hidden',
          }}>
            <div style={{
              background: '#171D29',
              color: 'white',
              padding: '16px 24px',
              fontFamily: '"Arial Black", Impact, sans-serif',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              fontSize: 14,
            }}>
              Something went wrong
            </div>
            <div style={{ height: 4, background: '#A1B808' }} />
            <div style={{ padding: '24px 28px' }}>
              <p style={{ marginTop: 0 }}>
                The app hit an unexpected error. Your data hasn't been lost — the page
                just needs a refresh. Please let the team know what you were doing.
              </p>
              <details style={{ marginTop: 12, fontSize: 13, color: '#4b5563' }}>
                <summary style={{ cursor: 'pointer' }}>Show error details</summary>
                <pre style={{
                  marginTop: 8,
                  padding: 12,
                  background: '#F3F3F3',
                  borderLeft: '3px solid #dc2626',
                  borderRadius: 4,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontSize: 12,
                  maxHeight: 240,
                  overflow: 'auto',
                }}>
                  {errMsg}
                  {this.state.errorInfo?.componentStack && (
                    '\n\nComponent stack:' + this.state.errorInfo.componentStack
                  )}
                </pre>
              </details>
              <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
                <button
                  onClick={this.handleReset}
                  style={{
                    background: 'white',
                    color: '#171D29',
                    border: '1px solid #171D29',
                    padding: '10px 18px',
                    fontFamily: '"Arial Black", Impact, sans-serif',
                    fontSize: 12,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    borderRadius: 3,
                    cursor: 'pointer',
                  }}
                >
                  Try Again
                </button>
                <button
                  onClick={this.handleReload}
                  style={{
                    background: '#A1B808',
                    color: 'white',
                    border: 'none',
                    padding: '10px 18px',
                    fontFamily: '"Arial Black", Impact, sans-serif',
                    fontSize: 12,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    borderRadius: 3,
                    cursor: 'pointer',
                  }}
                >
                  Reload Page
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
